import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { HtmlContent } from '@/components/ui/html-content';
import { ShoppingCart, Plus, Minus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: string;
  inStock: boolean;
  weight?: string;
  origin?: string;
}

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product, quantity: number) => void;
}

export const ProductCard = ({ product, onAddToCart }: ProductCardProps) => {
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();

  const handleAddToCart = () => {
    onAddToCart(product, quantity);
    toast.success(`${product.name} aggiunto al carrello`, {
      description: `Quantità: ${quantity}`,
    });
    setQuantity(1);
  };

  const handleQuantityChange = (delta: number) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  return (
    <Card className="group flex h-full flex-col overflow-hidden transition-all hover:shadow-lg">
      <div 
        className="relative aspect-square w-full cursor-pointer overflow-hidden bg-muted"
        onClick={() => navigate(`/prodotto/${product.id}`)}
      >
        <img
          src={product.image}
          alt={product.name}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
        {!product.inStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80">
            <Badge variant="destructive" className="text-sm">Esaurito</Badge>
          </div>
        )}
        {product.category && (
          <Badge className="absolute top-2 right-2" variant="secondary">
            <HtmlContent html={product.category} />
          </Badge>
        )}
      </div>
      
      <CardHeader>
        <CardTitle className="line-clamp-2">{product.name}</CardTitle>
        {product.description && (
          <CardDescription className="line-clamp-2">
            <HtmlContent html={product.description} maxLines={2} />
          </CardDescription>
        )}
        {product.weight && (
          <CardDescription className="text-xs text-muted-foreground">
            {product.weight}
          </CardDescription>
        )}
        {product.origin && (
          <CardDescription className="text-xs text-primary">
            {product.origin}
          </CardDescription>
        )}
      </CardHeader>
      
      <CardContent className="flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-primary">
            €{product.price.toFixed(2)}
          </span>
          {product.weight && (
            <span className="text-sm text-muted-foreground">
              / {product.weight}
            </span>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="flex flex-col gap-2">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleQuantityChange(-1)}
              disabled={quantity <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-8 text-center text-sm font-medium">{quantity}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleQuantityChange(1)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <Button
          className="w-full"
          onClick={handleAddToCart}
          disabled={!product.inStock}
        >
          <ShoppingCart className="mr-2 h-4 w-4" />
          {product.inStock ? 'Aggiungi al carrello' : 'Non disponibile'}
        </Button>
      </CardFooter>
    </Card>
  );
};

